
import React, { useEffect, useState } from "react";
import { db } from "../firebase";

import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from "firebase/firestore";

const AdminAddGroup = () => {
  const [groupName, setGroupName] = useState("");
  const [whatsappLink, setWhatsappLink] = useState("");
  const [type, setType] = useState("community");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState("");

  const [editingId, setEditingId] = useState(null);

  // =========================================
  // FETCH GROUPS AND CHANNELS
  // =========================================

  const fetchGroups = async () => {
    setFetching(true);

    try {
      const q = query(
        collection(db, "whatsupgroup"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);

      setGroups(
        snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }))
      );
    } catch (error) {
      console.error("Error fetching WhatsApp items:", error);
      setMessage("Imeshindikana kupakia orodha. Tafadhali jaribu tena.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // =========================================
  // VALIDATE WHATSAPP LINK
  // =========================================

  const isValidWhatsAppLink = (link, selectedType) => {
    try {
      const url = new URL(link);

      if (url.protocol !== "https:") {
        return false;
      }

      if (selectedType === "community") {
        return (
          url.hostname === "chat.whatsapp.com" &&
          /^\/[A-Za-z0-9]+\/?$/.test(url.pathname)
        );
      }

      if (selectedType === "channel") {
        return (
          ["whatsapp.com", "www.whatsapp.com"].includes(url.hostname) &&
          /^\/channel\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)
        );
      }

      return false;
    } catch {
      return false;
    }
  };

  // =========================================
  // RESET FORM
  // =========================================

  const resetForm = () => {
    setGroupName("");
    setWhatsappLink("");
    setType("community");
    setEditingId(null);
  };

  // =========================================
  // ADD OR UPDATE GROUP / CHANNEL
  // =========================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const name = groupName.trim();
    const link = whatsappLink.trim();

    if (!name || !link) {
      setMessage("Tafadhali jaza jina na WhatsApp link.");
      return;
    }

    if (!isValidWhatsAppLink(link, type)) {
      setMessage(
        type === "community"
          ? "Link si sahihi. Tumia https://chat.whatsapp.com/..."
          : "Link si sahihi. Tumia https://whatsapp.com/channel/..."
      );
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await updateDoc(doc(db, "whatsupgroup", editingId), {
          groupName: name,
          whatsappLink: link,
          type,
          updatedAt: serverTimestamp(),
        });

        setMessage("✅ Group/Channel imehaririwa kikamilifu.");
      } else {
        await addDoc(collection(db, "whatsupgroup"), {
          groupName: name,
          whatsappLink: link,
          type,
          createdAt: serverTimestamp(),
          active: true,
        });

        setMessage("✅ Group/Channel imeongezwa kikamilifu.");
      }

      resetForm();
      await fetchGroups();
    } catch (error) {
      console.error("Error saving WhatsApp item:", error);
      setMessage("❌ Imeshindikana kuhifadhi. Hakikisha ruhusa za Firestore.");
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // LOAD ITEM INTO EDIT FORM
  // =========================================

  const handleEdit = (item) => {
    setEditingId(item.id);
    setGroupName(item.groupName || "");
    setWhatsappLink(item.whatsappLink || "");
    setType(item.type === "channel" ? "channel" : "community");
    setMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================
  // ACTIVATE / DEACTIVATE
  // =========================================

  const toggleActive = async (item) => {
    setMessage("");

    try {
      await updateDoc(doc(db, "whatsupgroup", item.id), {
        active: item.active !== true,
        updatedAt: serverTimestamp(),
      });

      setMessage(
        item.active === true
          ? "Group/Channel imezimwa."
          : "Group/Channel imewashwa."
      );

      await fetchGroups();
    } catch (error) {
      console.error("Error changing status:", error);
      setMessage("Imeshindikana kubadilisha status.");
    }
  };

  // =========================================
  // DELETE ITEM
  // =========================================

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      `Una uhakika unataka kufuta "${item.groupName}"? Kitendo hiki hakiwezi kutenguliwa.`
    );

    if (!confirmed) return;

    setMessage("");

    try {
      await deleteDoc(doc(db, "whatsupgroup", item.id));

      if (editingId === item.id) {
        resetForm();
      }

      setMessage("✅ Group/Channel imefutwa.");
      await fetchGroups();
    } catch (error) {
      console.error("Error deleting WhatsApp item:", error);
      setMessage("❌ Imeshindikana kufuta. Jaribu tena.");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h2 style={styles.heading}>
          💬 Manage WhatsApp Groups & Channels
        </h2>

        {/* ADD / EDIT FORM */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formHeading}>
            {editingId ? "✏️ Edit Group / Channel" : "➕ Add New Group / Channel"}
          </h3>

          <label style={styles.label}>Aina ya WhatsApp</label>

          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={styles.input}
            required
          >
            <option value="community">WhatsApp Community Group</option>
            <option value="channel">WhatsApp Channel</option>
          </select>

          <label style={styles.label}>Jina</label>

          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder={
              type === "community"
                ? "Mfano: BFC WhatsApp Community"
                : "Mfano: BFC Official Channel"
            }
            style={styles.input}
            required
          />

          <label style={styles.label}>WhatsApp Link</label>

          <input
            type="url"
            value={whatsappLink}
            onChange={(e) => setWhatsappLink(e.target.value)}
            placeholder={
              type === "community"
                ? "https://chat.whatsapp.com/..."
                : "https://whatsapp.com/channel/..."
            }
            style={styles.input}
            required
          />

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.saveButton,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading
              ? "Inahifadhi..."
              : editingId
                ? "💾 Save Changes"
                : "➕ Add Group / Channel"}
          </button>

          {editingId && (
            <button
              type="button"
              style={styles.cancelButton}
              onClick={() => {
                resetForm();
                setMessage("");
              }}
            >
              Cancel Edit
            </button>
          )}

          {message && (
            <p style={styles.message} role="status">
              {message}
            </p>
          )}
        </form>

        {/* EXISTING ITEMS */}
        <div style={styles.listSection}>
          <div style={styles.listHeader}>
            <h3 style={styles.formHeading}>
              📋 Existing Groups & Channels
            </h3>

            <button
              type="button"
              onClick={fetchGroups}
              style={styles.refreshButton}
              disabled={fetching}
            >
              ↻ Refresh
            </button>
          </div>

          {fetching ? (
            <p style={styles.info}>⏳ Inapakia orodha...</p>
          ) : groups.length === 0 ? (
            <p style={styles.info}>
              Hakuna Groups au Channels zilizoongezwa bado.
            </p>
          ) : (
            <div style={styles.list}>
              {groups.map((item) => (
                <div key={item.id} style={styles.card}>
                  <div style={styles.itemTop}>
                    <div style={styles.itemIcon}>
                      {item.type === "channel" ? "📢" : "👥"}
                    </div>

                    <div style={styles.itemDetails}>
                      <h4 style={styles.itemName}>
                        {item.groupName || "WhatsApp"}
                      </h4>

                      <p style={styles.itemType}>
                        {item.type === "channel"
                          ? "WhatsApp Channel"
                          : "WhatsApp Community Group"}
                      </p>

                      <span
                        style={{
                          ...styles.status,
                          background:
                            item.active === true ? "#164e32" : "#512727",
                          color:
                            item.active === true ? "#86efac" : "#fca5a5",
                        }}
                      >
                        {item.active === true ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>
                  </div>

                  <p style={styles.linkText}>
                    {item.whatsappLink}
                  </p>

                  <div style={styles.actions}>
                    <button
                      type="button"
                      style={styles.editButton}
                      onClick={() => handleEdit(item)}
                    >
                      ✏️ Edit
                    </button>

                    <button
                      type="button"
                      style={styles.toggleButton}
                      onClick={() => toggleActive(item)}
                    >
                      {item.active === true ? "⏸ Deactivate" : "▶ Activate"}
                    </button>

                    <button
                      type="button"
                      style={styles.deleteButton}
                      onClick={() => handleDelete(item)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    boxSizing: "border-box",
    padding: "20px 12px 110px",
    background: "#101522",
    color: "#fff",
  },
  container: {
    maxWidth: 700,
    width: "100%",
    margin: "0 auto",
  },
  heading: {
    textAlign: "center",
    color: "#25d366",
    fontSize: 22,
    marginBottom: 22,
  },
  form: {
    padding: 18,
    background: "#202638",
    border: "1px solid #344158",
    borderRadius: 12,
    marginBottom: 25,
  },
  formHeading: {
    margin: "0 0 16px",
    color: "#25d366",
    fontSize: 17,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontSize: 13,
    color: "#d1d5db",
  },
  input: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    padding: 12,
    marginBottom: 16,
    borderRadius: 7,
    border: "1px solid #596579",
    background: "#fff",
    color: "#111",
    fontSize: 14,
  },
  saveButton: {
    width: "100%",
    padding: 13,
    border: 0,
    borderRadius: 8,
    background: "#25d366",
    color: "#073b20",
    fontWeight: "bold",
    cursor: "pointer",
  },
  cancelButton: {
    width: "100%",
    padding: 11,
    marginTop: 8,
    border: "1px solid #64748b",
    borderRadius: 8,
    background: "#344158",
    color: "#fff",
    cursor: "pointer",
  },
  message: {
    textAlign: "center",
    overflowWrap: "anywhere",
    fontSize: 13,
    marginBottom: 0,
  },
  listSection: {
    padding: 15,
    background: "#171e2e",
    border: "1px solid #344158",
    borderRadius: 12,
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 15,
  },
  refreshButton: {
    padding: "8px 11px",
    border: "1px solid #475569",
    borderRadius: 7,
    background: "#263249",
    color: "#fff",
    cursor: "pointer",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    minWidth: 0,
    padding: 13,
    background: "#202a3c",
    border: "1px solid #344158",
    borderRadius: 10,
  },
  itemTop: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  itemIcon: {
    fontSize: 25,
  },
  itemDetails: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    margin: "0 0 5px",
    fontSize: 15,
    overflowWrap: "anywhere",
  },
  itemType: {
    margin: "0 0 8px",
    color: "#aab5c5",
    fontSize: 12,
  },
  status: {
    display: "inline-block",
    padding: "4px 7px",
    borderRadius: 5,
    fontSize: 10,
    fontWeight: "bold",
  },
  linkText: {
    color: "#93c5fd",
    fontSize: 11,
    overflowWrap: "anywhere",
    margin: "12px 0",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
  },
  editButton: {
    padding: "8px 10px",
    border: 0,
    borderRadius: 6,
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
  },
  toggleButton: {
    padding: "8px 10px",
    border: 0,
    borderRadius: 6,
    background: "#475569",
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
  },
  deleteButton: {
    padding: "8px 10px",
    border: 0,
    borderRadius: 6,
    background: "#dc2626",
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
  },
  info: {
    textAlign: "center",
    color: "#cbd5e1",
    padding: 15,
    fontSize: 13,
  },
};

export default AdminAddGroup;
